
export type ContentResponse = {
    content: string;
};

export type NeedleInput = {
    needle: string;
    includeKeyPhrase: boolean;
    type: string;
    wordCount: number;
    paragraphCount?: number | null;
    content?: string | null;
};

export interface Subscription {
    id: number | null;
    domainName: string;
    customerId: number | null;
  }
  
  export const initialSubscriptionFormData: Subscription = {
    id: null,
    domainName: '',
    customerId: null,
  };
  
  export interface Customer {
    id: number | null;
    name: string | null;
    company: string | null;
    login: number | null;
    email: string | null;
  }
  
  export interface CustomerFormData {
    company: string;
    name: string;
    login: string;
    email: string;
  }
  
  export const initialCustomerFormData: CustomerFormData = {
    company: '',
    name: '',
    login: '',
    email: '',
  };
  
  export interface WebsiteAcceptanceDefaults {
    deliveryModel?: string | null;
    releaseVersion?: string | null;
    releaseIdentifier?: string | null;
    releaseDate?: string | null;
    reviewUrl?: string | null;
    productionUrl?: string | null;
    clientLegalName?: string | null;
    clientCompanyNumber?: string | null;
    clientRegisteredAddress?: string | null;
    clientNoticeEmail?: string | null;
    masterAgreementReference?: string | null;
    thirdPartyDeveloper?: string | null;
    acceptanceEvidence?: string | null;
    notes?: string | null;
    knownIssues?: string | null;
    goLiveAuthorisation?: string | null;
    approvedGoLiveWindow?: string | null;
    supportAllowance?: string | null;
    additionalSupportRate?: string | null;
    savedAt?: string | null;
  }

  export interface Website {
    id: number | null;
    name: string;
    projectCode: string;
    pleskCustomerId: number | null;
    pleskSubscription: string | null;
    prodDomain: string | null;
    prodDomainError: string | null;
    isInternal: boolean;
    excludeFromDailyUpdates: boolean;
    testDomain: string | null;
    testBasicAuthUser: string | null;
    testBasicAuthPass: string | null;
    contractId: number | null;
    gitRepoUrl: string | null;
    gitUser: string | null;
    gitPass: string | null;
    dockerRepoUrl: string | null;
    type: 'wordpress' | 'symfony' | 'laravel' | 'other';
    prodDatabaseName: string | null;
    testDatabaseName: string | null;
    prodDatabaseUser: string | null;
    testDatabaseUser: string | null;
    prodDatabasePass: string | null;
    testDatabasePass: string | null;
    latestAcceptance?: WebsiteAcceptanceSummary | null;
    acceptanceDefaults?: WebsiteAcceptanceDefaults | null;
  }

  export interface WebsiteAcceptanceDocument {
    id: number;
    type: string;
    filename?: string | null;
    sharepointWebUrl?: string | null;
  }

  export interface WebsiteAcceptanceSummary {
    clientEntityType?: string | null;
    signerTitle?: string | null;
    releaseScope?: string | null;
    workReferences?: string | null;
    issuesReviewedBy?: string | null;
    issuesReviewDate?: string | null;
    knownIssuesReviewStatus?: string | null;
    exclusions?: string | null;

    id: number;
    websiteId?: number;
    status: string;
    deliveryModel?: string | null;
    releaseVersion: string;
    releaseIdentifier?: string | null;
    releaseDate?: string | null;
    reviewUrl?: string | null;
    productionUrl?: string | null;
    clientLegalName?: string | null;
    clientCompanyNumber?: string | null;
    clientRegisteredAddress?: string | null;
    clientNoticeEmail?: string | null;
    masterAgreementReference?: string | null;
    thirdPartyDeveloper?: string | null;
    acceptanceEvidence?: string | null;
    knownIssues?: string | null;
    goLiveAuthorisation?: string | null;
    approvedGoLiveWindow?: string | null;
    supportAllowance?: string | null;
    additionalSupportRate?: string | null;
    signerName?: string | null;
    signerEmail?: string | null;
    notes?: string | null;
    sentAt?: string | null;
    acceptedAt?: string | null;
    documents?: WebsiteAcceptanceDocument[];
    docusignEnvelope?: {
      envelopeId?: string | null;
      status?: string | null;
      signerName?: string | null;
      signerEmail?: string | null;
      sentAt?: string | null;
      completedAt?: string | null;
    } | null;
    contractGeneration?: {
      id: number;
      referenceNumber?: string | null;
      outputBasename?: string | null;
      documents?: WebsiteAcceptanceDocument[];
      docusignEnvelope?: WebsiteAcceptanceSummary['docusignEnvelope'];
    } | null;
  }
  
  export interface BitbucketRepo {
    slug: string;
    isPrivate: boolean;
  }
  
export const initialWebsiteFormData = {
  name: '',
  projectCode: '',
  pleskCustomerId: 0,
  pleskSubscription: '',
  prodDomain: '',
  prodDomainError: '',
  isInternal: false,
  excludeFromDailyUpdates: false,
  testDomain: '',
  testBasicAuthUser: '',
  testBasicAuthPass: '',
  contractId: 0,
  gitRepoUrl: '',
  gitUser: '',
  gitPass: '',
  dockerRepoUrl: '',
  type: 'wordpress',
  prodDatabaseName: '',
  testDatabaseName: '',
  prodDatabaseUser: '',
  testDatabaseUser: '',
  prodDatabasePass: '',
  testDatabasePass: '',
};

export type BillingType = 'time_and_materials' | 'fixed';

export interface FreeAgentProjectDetails {
  url?: string | null;
  name?: string | null;
  status?: string | null;
  contact?: string | null;
  contactName?: string | null;
  currency?: string | null;
  budget?: number | null;
  budgetUnits?: string | null;
  hoursPerDay?: number | null;
  normalBillingRate?: number | null;
  billingPeriod?: string | null;
  usesProjectInvoiceSequence?: boolean | null;
  includeUnbilledTimeInProfitability?: boolean | null;
  isIr35?: boolean | null;
  startsOn?: string | null;
  endsOn?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isDeletable?: boolean | null;
  [key: string]: unknown;
}

export interface FreeAgentUserDetails {
  id?: string | null;
  url?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  [key: string]: unknown;
}

export interface Project {
  projectId: number;
  freeagentProjectUrl: string | null;
  billingType: BillingType | null;
  projectManagerUrl: string | null;
  freeagentProject?: FreeAgentProjectDetails | null;
  projectManager?: FreeAgentUserDetails | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ProjectFormData {
  freeagentProjectUrl: string;
  billingType: BillingType;
  projectManagerUrl: string;
}

export const initialProjectFormData: ProjectFormData = {
  freeagentProjectUrl: '',
  billingType: 'time_and_materials',
  projectManagerUrl: '',
};

export type CategoryType = 'averaging' | 'regular' | 'irregular';

export interface FreeAgentCategory {
  url?: string;
  description?: string;
  nominalCode?: string;
  groupDescription?: string | null;
  allowableForTax?: boolean | null;
  taxReportingName?: string | null;
  autoSalesTaxRate?: string | null;
  bankAccount?: string | null;
  capitalAssetType?: string | null;
  stockItem?: string | null;
  hirePurchase?: string | null;
  user?: string | null;
  categoryGroup?: string | null;
  [key: string]: unknown;
}

export type FreeAgentCategoryGroups = Record<string, FreeAgentCategory[]>;

export interface Category {
  id: number;
  categoryUrl: string;
  type: CategoryType;
  categoryName?: string | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
  interval?: number | string | null;
  amount?: number | string | null;
  freeagentCategory?: FreeAgentCategory | null;
}

export interface CategoryFormData {
  categoryName: string;
  categoryUrl: string;
  type: CategoryType;
  startDateTime: string;
  endDateTime: string;
  interval: string;
  amount: string;
}

export const initialCategoryFormData: CategoryFormData = {
  categoryName: '',
  categoryUrl: '',
  type: 'regular',
  startDateTime: '',
  endDateTime: '',
  interval: '',
  amount: '',
};

export interface CashEvent {
  id?: number;
  categoryId?: number | null;
  categoryUrl?: string | null;
  categoryName?: string | null;
  categoryType?: CategoryType | string | null;
  description?: string | null;
  notes?: string | null;
  amount?: number | string | null;
  occursOn?: string | null;
  occursAt?: string | null;
  dueOn?: string | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  [key: string]: unknown;
}
